import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Switch,
  Slider,
  Button,
  Alert,
  Divider,
  Grid
} from '@mui/material';
import { Save, Sync } from '@mui/icons-material';
import api from '../../../../../utils/api';

const SyncSettings = ({ connection, onUpdate }) => {
  const [settings, setSettings] = useState({
    syncEnabled: connection?.syncEnabled || true,
    syncDirection: connection?.syncDirection || 'bidirectional',
    autoSync: connection?.autoSync || true,
    syncInterval: connection?.syncInterval || 300,
    webhooksEnabled: connection?.webhooksEnabled || true
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (connection) {
      setSettings({
        syncEnabled: connection.syncEnabled,
        syncDirection: connection.syncDirection,
        autoSync: connection.autoSync,
        syncInterval: connection.syncInterval,
        webhooksEnabled: connection.webhooksEnabled
      });
    }
  }, [connection]);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      const response = await api.put(`/shopify/connections/${connection.id}`, settings);

      if (response.data.success) {
        setSuccess('Settings saved successfully');
        if (onUpdate) {
          onUpdate(response.data.data);
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const formatInterval = (seconds) => {
    if (seconds < 60) return `${seconds} seconds`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes`;
    return `${Math.floor(seconds / 3600)} hours`;
  };

  return (
    <Box>
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Synchronization Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure how data syncs between Shopify and DymenStock
          </Typography>

          <Grid container spacing={3}>
            {/* Sync Enabled */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.syncEnabled}
                    onChange={(e) => setSettings({ ...settings, syncEnabled: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Enable Synchronization</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Turn on/off all sync operations
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Sync Direction */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Sync Direction
              </Typography>
              <FormControl component="fieldset">
                <RadioGroup
                  value={settings.syncDirection}
                  onChange={(e) => setSettings({ ...settings, syncDirection: e.target.value })}
                >
                  <FormControlLabel
                    value="shopify_to_dymen"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">Shopify → DymenStock</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Import data from Shopify only
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="dymen_to_shopify"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">DymenStock → Shopify</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Export data to Shopify only
                        </Typography>
                      </Box>
                    }
                  />
                  <FormControlLabel
                    value="bidirectional"
                    control={<Radio />}
                    label={
                      <Box>
                        <Typography variant="body2">Bidirectional</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Sync changes in both directions
                        </Typography>
                      </Box>
                    }
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Auto Sync */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.autoSync}
                    onChange={(e) => setSettings({ ...settings, autoSync: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Automatic Sync</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Automatically sync at regular intervals
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Sync Interval */}
            {settings.autoSync && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  Sync Interval: {formatInterval(settings.syncInterval)}
                </Typography>
                <Slider
                  value={settings.syncInterval}
                  onChange={(e, value) => setSettings({ ...settings, syncInterval: value })}
                  min={60}
                  max={3600}
                  step={60}
                  marks={[
                    { value: 60, label: '1m' },
                    { value: 300, label: '5m' },
                    { value: 900, label: '15m' },
                    { value: 1800, label: '30m' },
                    { value: 3600, label: '1h' }
                  ]}
                />
                <Typography variant="caption" color="text.secondary">
                  How often to automatically sync data
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <Divider />
            </Grid>

            {/* Webhooks */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.webhooksEnabled}
                    onChange={(e) => setSettings({ ...settings, webhooksEnabled: e.target.checked })}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1">Real-time Webhooks</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Receive instant updates when data changes in Shopify
                    </Typography>
                  </Box>
                }
              />
            </Grid>

            {/* Save Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card sx={{ mt: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Performance Tips
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Webhooks</strong> provide real-time updates and are more efficient than polling
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Longer intervals</strong> reduce API calls but delay synchronization
            </Typography>
            <Typography component="li" variant="body2" sx={{ mb: 1 }}>
              <strong>Bidirectional sync</strong> requires careful conflict resolution
            </Typography>
            <Typography component="li" variant="body2">
              <strong>One-way sync</strong> is simpler and faster for most use cases
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SyncSettings;
